import React from 'react';
import AdminNavbar from "../../Navbars/AdminNavbar.js";
import AdminSidebar from '../../Sidebar/AdminSidebar';
import { ApiGatewayApis } from '../../../apis/ApiGatewayApis';
import { useNavigate } from 'react-router-dom';
import { Oval } from 'react-loader-spinner';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
    ArrowLeft, RefreshCw, Activity, TrendingUp,
    AlertTriangle, Users, Clock, Zap,
} from 'lucide-react';

ChartJS.register(
    CategoryScale, LinearScale, PointElement, LineElement,
    BarElement, ArcElement, Title, Tooltip, Legend
);

function AnalyticsDashboard() {
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState<boolean>(true);
    const [overview, setOverview] = React.useState<any>(null);
    const [requestData, setRequestData] = React.useState<any>(null);
    const [topConsumers, setTopConsumers] = React.useState<any[]>([]);
    const [recentErrors, setRecentErrors] = React.useState<any[]>([]);
    const [period, setPeriod] = React.useState<number>(30);
    const [source, setSource] = React.useState<string>('');

    React.useEffect(() => { loadAnalytics(); }, [period, source]);

    const loadAnalytics = () => {
        setLoading(true);
        Promise.all([
            ApiGatewayApis.getAnalyticsOverview(period),
            ApiGatewayApis.getAnalyticsRequests(period, source || undefined),
            ApiGatewayApis.getTopConsumers(period, 10),
            ApiGatewayApis.getRecentErrors(24, 20),
        ]).then(([overviewRes, requestsRes, consumersRes, errorsRes]: any[]) => {
            setLoading(false);
            if (overviewRes?.data?.data) setOverview(overviewRes.data.data);
            if (requestsRes?.data?.data) setRequestData(requestsRes.data.data);
            if (consumersRes?.data?.data) setTopConsumers(consumersRes.data.data || []);
            if (errorsRes?.data?.data) setRecentErrors(errorsRes.data.data || []);
        }).catch((error: any) => {
            setLoading(false);
            toast.error('Failed to load analytics');
        });
    };

    // Chart: Requests over time
    const requestChartData = {
        labels: requestData?.daily_breakdown?.map((d: any) => d.date) || [],
        datasets: [
            {
                label: 'Total Requests',
                data: requestData?.daily_breakdown?.map((d: any) => d.total) || [],
                borderColor: '#E04343',
                backgroundColor: '#F9E8E899',
                tension: 0.3,
            },
            {
                label: 'Errors',
                data: requestData?.daily_breakdown?.map((d: any) => d.errors) || [],
                borderColor: '#EF4444',
                backgroundColor: '#FEE2E299',
                tension: 0.3,
            },
        ],
    };

    const requestChartOptions = {
        responsive: true,
        plugins: {
            legend: { position: 'top' as const },
            title: { display: true, text: `API Requests (Last ${period} days)` },
        },
    };

    // Chart: Top consumers bar
    const consumerChartData = {
        labels: topConsumers.slice(0, 8).map((c: any) => c.name || c.key_prefix || 'Unknown'),
        datasets: [
            {
                label: 'Requests',
                data: topConsumers.slice(0, 8).map((c: any) => c.total_requests),
                backgroundColor: [
                    '#E04343', '#3B82F6', '#10B981', '#8B5CF6',
                    '#F59E0B', '#EC4899', '#06B6D4', '#84CC16',
                ],
            },
        ],
    };

    // Chart: Status code distribution
    const statusDistribution = requestData?.status_distribution || {};
    const statusChartData = {
        labels: Object.keys(statusDistribution).map(code => `${code}xx`),
        datasets: [{
            data: Object.values(statusDistribution),
            backgroundColor: ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'],
        }],
    };

    const getStatusColor = (status: number) => {
        if (status >= 200 && status < 300) return 'text-green-600';
        if (status >= 400 && status < 500) return 'text-yellow-600';
        if (status >= 500) return 'text-red-600';
        return 'text-gray-600';
    };

    return (
        <>
            <AdminSidebar title="Analytics" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="API Analytics" />

                <div className='lg:px-10 pb-6 px-6 pt-5 bg-[#FAFAFA] min-h-screen'>

                    {/* Header */}
                    <div className='flex justify-between items-center mb-5'>
                        <div className='flex items-center gap-3'>
                            <button onClick={() => navigate('/admin/api-gateway')} className='text-gray-500 hover:text-[#E04343]'>
                                <ArrowLeft size={20} />
                            </button>
                            <h1 className='text-[20px] font-bold text-[#263238]'>API Analytics</h1>
                        </div>
                        <div className='flex items-center gap-3'>
                            <select value={source} onChange={(e) => setSource(e.target.value)}
                                className='bg-white border border-[#DADADA] text-sm rounded-[5px] px-3 py-2'>
                                <option value=''>All Sources</option>
                                <option value='partner'>Partner API</option>
                                <option value='customer'>Customer API</option>
                            </select>
                            <select value={period} onChange={(e) => setPeriod(parseInt(e.target.value))}
                                className='bg-white border border-[#DADADA] text-sm rounded-[5px] px-3 py-2'>
                                <option value={7}>Last 7 days</option>
                                <option value={30}>Last 30 days</option>
                                <option value={90}>Last 90 days</option>
                            </select>
                            <button onClick={loadAnalytics}
                                className='bg-white border border-[#DADADA] p-2 rounded-[5px] hover:bg-gray-50' title='Refresh'>
                                <RefreshCw size={18} className={loading ? 'animate-spin text-[#E04343]' : 'text-gray-600'} />
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className='flex justify-center py-20'>
                            <Oval visible={true} height="40" width="40" color="#E04343" secondaryColor="#FCBF94" />
                        </div>
                    ) : (
                        <>
                            {/* Stats Row */}
                            <div className='grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-4 mb-6'>
                                <div className='bg-white shadow-sm rounded-[10px] px-[18px] pt-[18px] pb-[14px] border-t-[2px] border-[#E04343]'>
                                    <h5 className='text-[12px] text-[#808080] flex items-center gap-1'><Activity size={14} /> Total Requests</h5>
                                    <h1 className='text-[24px] font-semibold mt-1'>
                                        {overview?.api_requests?.total?.toLocaleString() || 0}
                                    </h1>
                                </div>
                                <div className='bg-white shadow-sm rounded-[10px] px-[18px] pt-[18px] pb-[14px] border-t-[2px] border-[#E04343]'>
                                    <h5 className='text-[12px] text-[#808080] flex items-center gap-1'><Clock size={14} /> Avg Response</h5>
                                    <h1 className='text-[24px] font-semibold mt-1'>
                                        {overview?.api_requests?.avg_response_time || 0}ms
                                    </h1>
                                </div>
                                <div className='bg-white shadow-sm rounded-[10px] px-[18px] pt-[18px] pb-[14px] border-t-[2px] border-[#E04343]'>
                                    <h5 className='text-[12px] text-[#808080] flex items-center gap-1'><AlertTriangle size={14} /> Error Rate</h5>
                                    <h1 className='text-[24px] font-semibold mt-1'>
                                        {overview?.api_requests?.total
                                            ? ((overview.api_requests.errors / overview.api_requests.total) * 100).toFixed(1)
                                            : 0}%
                                    </h1>
                                </div>
                                <div className='bg-white shadow-sm rounded-[10px] px-[18px] pt-[18px] pb-[14px] border-t-[2px] border-[#E04343]'>
                                    <h5 className='text-[12px] text-[#808080] flex items-center gap-1'><Zap size={14} /> Success Rate</h5>
                                    <h1 className='text-[24px] font-semibold mt-1'>
                                        {overview?.api_requests?.total
                                            ? (((overview.api_requests.total - overview.api_requests.errors) / overview.api_requests.total) * 100).toFixed(1)
                                            : 0}%
                                    </h1>
                                </div>
                            </div>

                            {/* Charts Row */}
                            <div className='grid lg:grid-cols-3 gap-4 mb-6'>
                                {/* Request Trend Chart */}
                                <div className='lg:col-span-2 bg-white shadow-sm rounded-[10px] p-5'>
                                    <Line options={requestChartOptions} data={requestChartData} />
                                </div>

                                {/* Status Distribution */}
                                <div className='bg-white shadow-sm rounded-[10px] p-5'>
                                    <h3 className='text-[14px] font-bold mb-3'>Status Distribution</h3>
                                    {Object.keys(statusDistribution).length > 0 ? (
                                        <Doughnut data={statusChartData} options={{ plugins: { legend: { position: 'bottom' } } }} />
                                    ) : (
                                        <p className='text-[13px] text-[#808080] text-center py-10'>No data available</p>
                                    )}
                                </div>
                            </div>

                            {/* Top Consumers + Recent Errors Row */}
                            <div className='grid lg:grid-cols-2 gap-4'>
                                {/* Top Consumers */}
                                <div className='bg-white shadow-sm rounded-[10px] p-5'>
                                    <h3 className='text-[14px] font-bold mb-3 flex items-center gap-2'>
                                        <Users size={16} /> Top API Consumers
                                    </h3>
                                    {topConsumers.length > 0 ? (
                                        <Bar data={consumerChartData} options={{
                                            indexAxis: 'y' as const,
                                            plugins: { legend: { display: false } },
                                            responsive: true,
                                        }} />
                                    ) : (
                                        <p className='text-[13px] text-[#808080] text-center py-10'>No consumer data</p>
                                    )}
                                </div>

                                {/* Recent Errors */}
                                <div className='bg-white shadow-sm rounded-[10px] p-5'>
                                    <h3 className='text-[14px] font-bold mb-3 flex items-center gap-2'>
                                        <AlertTriangle size={16} className='text-red-500' /> Recent Errors (24h)
                                    </h3>
                                    {recentErrors.length > 0 ? (
                                        <div className='space-y-2 max-h-[400px] overflow-y-auto'>
                                            {recentErrors.map((error: any, idx: number) => (
                                                <div key={idx} className='bg-gray-50 rounded-lg px-3 py-2 border-l-[3px] border-red-400'>
                                                    <div className='flex justify-between items-start'>
                                                        <div>
                                                            <span className={`text-[12px] font-bold ${getStatusColor(error.status_code)}`}>
                                                                {error.status_code}
                                                            </span>
                                                            <span className='text-[12px] text-[#808080] ml-2'>
                                                                {error.method} {error.path}
                                                            </span>
                                                        </div>
                                                        <span className='text-[11px] text-[#808080]'>
                                                            {error.created_at ? new Date(error.created_at).toLocaleTimeString() : ''}
                                                        </span>
                                                    </div>
                                                    {error.error_message && (
                                                        <p className='text-[11px] text-red-600 mt-1 truncate'>{error.error_message}</p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className='text-center py-10'>
                                            <span className='text-[32px]'>&#127881;</span>
                                            <p className='text-[13px] text-[#808080] mt-2'>No errors in the last 24 hours</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
        </>
    );
}

export default AnalyticsDashboard;